import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, ListItem } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { ListCell, ListHeader, ListRow, ViewNotesModal } from '/src/components';
import { usePaymentStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { Note, Payment } from '/src/lib/models';
import { TListPaymentsRequest, Totals } from '/src/lib/types';
import * as utils from '/src/utils';

export type PaymentsListProps = {
  /**
   * List of Payments to display
   */
  data?: Payment[];
  /**
   * Query to include when fetching Payments
   */
  query?: TListPaymentsRequest;
};

/**
 * Payments List
 */
export const PaymentsList: React.FC<PaymentsListProps> = observer(({ data, query }) => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;
  const rowHeight = 48;

  /** Context **/
  const paymentStore = usePaymentStore();

  /** State **/
  const [payments, setPayments] = useState<Payment[]>([]);
  const [totals, setTotals] = useState<Totals>({});
  const [resolved, setResolved] = useState(false);
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  // View Notes popup shows if `notes` is defined
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/

  const listData = useMemo(() => {
    if (payments) {
      const list = payments.toSorted((a, b) => ((a as any)[sortType] > (b as any)[sortType] ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [payments, sortType, sortReverse]);

  /** Methods **/

  const formatData = (data: Payment[]) => {
    // Prepare totals
    const totals: Totals = {};
    totals.amount = 0;

    const payments = data.map((payment) => {
      // Calculate totals
      totals.amount += payment.amount;

      // Store
      return payment;
    });

    // Save data
    setPayments(payments);
    setTotals(totals);

    // Set as resolved
    setResolved(true);
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /**
   * Sets sortType and sortReverse based on given sortType
   * @param type string sortType to use
   */
  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (data) {
      formatData(data);
    } else if (query && Object.values(query).length) {
      paymentStore.query(query).then((payments) => formatData(payments ?? []));
    }
  }, [data, query]);

  /** Render **/
  return (
    <Box>
      {/* HEADER */}
      {!!listData.length && (
        <ListHeader
          items={[
            { flex: 5, propName: 'notes', label: 'Notes' },
            { propName: 'name', label: 'Name' },
            { propName: 'amount', label: 'Amount', textAlign: 'right' },
            { propName: 'datePaid', label: 'Date Paid', textAlign: 'right' },
            { propName: 'dateDeposited', label: 'Date Deposited', textAlign: 'right' },
          ]}
          sortType={sortType}
          sortReverse={sortReverse}
          sortList={sortList}
        />
      )}

      {!!listData.length && <Divider />}

      {/* LIST */}
      {resolved && !!listData.length && (
        <Box component="md-content" height={listData.length * rowHeight + 'px'} maxHeight={9 * rowHeight + 'px'}>
          {listData.map((payment, i) => (
            <ListRow
              key={payment._id}
              items={[
                {
                  propName: 'notes',
                  isNotes: true,
                  flex: 5,
                  hide: !payment.notes?.length,
                  showNotesPopup: () => showNotesPopup(payment.notes, payment.name),
                },
                { propName: 'name', content: payment.name, url: `/payments/${payment._id}` },
                { propName: 'amount', content: utils.currency.format(payment.amount), textAlign: 'right' },
                { propName: 'datePaid', content: dayjs(payment.datePaid).format(mediumDate), textAlign: 'right' },
                {
                  propName: 'dateDeposited',
                  content: dayjs(payment.dateDeposited).format(mediumDate),
                  textAlign: 'right',
                },
              ]}
              rowIndex={i}
            />
          ))}
        </Box>
      )}

      <Divider />

      {/* TOTALS */}
      {!!listData.length && (
        <ListItem sx={{ height: rowHeight }}>
          <Box
            display="flex"
            flex="100"
            justifyContent="space-between"
            alignItems="center"
            className={`medium md-subhead-sm text-center ${listData.length > 8 && 'padForScrollbar'}`}
          >
            {[
              { flex: 5, propName: 'notes', label: 'Notes' },
              { propName: 'name', label: 'Name' },
              { propName: 'amount', label: 'Amount', textAlign: 'right' },
              { propName: 'datePaid', label: 'Date Paid', textAlign: 'right' },
              { propName: 'dateDeposited', label: 'Date Deposited', textAlign: 'right' },
            ].map(({ propName, ...itemProps }, i) => (
              <ListCell
                key={`${propName}-${i}`}
                content={i === 0 ? 'Totals' : totals[propName] ? utils.currency.format(totals[propName]) : ''}
                {...itemProps}
              />
            ))}
          </Box>
        </ListItem>
      )}

      {resolved && !listData.length && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <Box component="span">No Payments found.</Box>
          </Box>
        </ListItem>
      )}

      {!resolved && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <CircularProgress className="md-primary" md-mode="indeterminate" />
          </Box>
        </ListItem>
      )}

      {notes && (
        <ViewNotesModal
          notes={notes}
          name={notesName}
          open={!!notes}
          setOpen={(open) => !open && setNotes(undefined)}
        />
      )}
    </Box>
  );
});
